import * as React from 'react'

import { Info } from '@syconium/little-miss-figgy'

import { LogoDigitizationSetting } from '../../../../../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroideryPrices } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryPricingProvider'
import { useEmbroiderySiteMessages } from '../../../../../../containers/EmbroideryContainer/useEmbroiderySiteMessages'
import { RequiredAsterisk } from '../../../../../atoms/RequiredAsterisk'
import { SelectedOptionText, StyledTooltip } from '../../../primitives/shared-form-styles'
import { ValidationMessage } from '../../../primitives/ValidationMessage/ValidationMessage'

import {
  ColorOptionsHeader,
  OptionList,
  OptionPrice,
  OptionRadio,
  OptionRow,
  TooltipButton,
} from './styles'

export type LogoColorOptionsProps = {
  selectedSetting: LogoDigitizationSetting | undefined
  onSelectSetting(setting: LogoDigitizationSetting): void
  errorMessage?: string
}

export const LogoColorOptions: React.FC<LogoColorOptionsProps> = ({
  selectedSetting,
  onSelectSetting,
  errorMessage,
}) => {
  const { formatPrice, getPriceInCents } = useEmbroideryPrices()
  const { logoColorSelectionTooltip } = useEmbroiderySiteMessages()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: copy },
      },
    },
  } = useTranslation()

  const name = React.useId()
  const options = [
    {
      setting: 'BLACK_WHITE',
      label: copy.logoStrings.LOGO_BLACK_WHITE.title,
      priceLabel: formatPrice(getPriceInCents('logo:bw'), true),
    },
    {
      setting: 'COLOR',
      label: copy.logoStrings.LOGO_COLOR.title,
      priceLabel: formatPrice(getPriceInCents('logo:full_color'), true),
    },
  ] as const

  const selectedLabel = options.find(option => option.setting === selectedSetting)?.label

  return (
    <div
      role='group'
      aria-labelledby={`${name}-label`}
      aria-describedby={errorMessage === undefined ? undefined : `${name}-error`}
    >
      <ColorOptionsHeader as='div' id={`${name}-label`}>
        <span>
          {copy.logoColorLabel}
          <RequiredAsterisk />
        </span>

        {logoColorSelectionTooltip !== undefined && (
          <StyledTooltip content={<>{logoColorSelectionTooltip}</>} placement='bottom-start'>
            <TooltipButton type='button' aria-label={copy.ariaLabelLogoColorTooltip}>
              <Info height='12px' width='12px' />
            </TooltipButton>
          </StyledTooltip>
        )}

        <SelectedOptionText>{selectedLabel}</SelectedOptionText>
      </ColorOptionsHeader>

      <OptionList>
        {options.map(option => (
          <OptionRow key={option.setting}>
            <OptionRadio
              name={name}
              value={option.setting}
              checked={selectedSetting === option.setting}
              onChange={() => onSelectSetting(option.setting)}
            >
              {option.label}
            </OptionRadio>
            <OptionPrice as='span'>{option.priceLabel}</OptionPrice>
          </OptionRow>
        ))}
      </OptionList>

      <ValidationMessage id={`${name}-error`} message={errorMessage} />
    </div>
  )
}
